/**
 * SigV4-signed fetch wrapper for the admin REST API. Uses aws4fetch so the
 * signing lives entirely in the browser — no middleware, no proxy.
 */

import { AwsClient } from 'aws4fetch';
import { getAwsCredentials } from './auth';
import { getConfig } from './config';

async function client(): Promise<AwsClient> {
  const cfg = getConfig();
  const creds = await getAwsCredentials();
  return new AwsClient({
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    sessionToken: creds.sessionToken,
    service: 'execute-api',
    region: cfg.region,
  });
}

async function apiFetch(pathAndQuery: string, init: RequestInit = {}): Promise<Response> {
  const cfg = getConfig();
  const c = await client();
  const url = new URL(pathAndQuery.replace(/^\//, ''), cfg.adminApiUrl).toString();
  const res = await c.fetch(url, init);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res;
}

async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  return (await res.json()) as T;
}

export interface ReportSummary {
  PK: string;
  SK: string;
  reportId: string;
  kind: 'bug-report' | 'player-report';
  receivedAt: number;
  gameId?: string | null;
  // bug-report fields
  description?: string;
  clientTimestamp?: number;
  // player-report fields
  reporterPlayerId?: string;
  reportedPlayerId?: string;
  reason?: string;
}

export interface ListReportsResponse {
  items: ReportSummary[];
  nextCursor: string | null;
}

export async function listReports(
  kind: 'bug-report' | 'player-report',
  cursor?: string,
): Promise<ListReportsResponse> {
  const qs = new URLSearchParams({ kind });
  if (cursor) qs.set('cursor', cursor);
  const res = await apiFetch(`reports?${qs.toString()}`);
  return res.json();
}

export async function getReport(reportId: string): Promise<Record<string, unknown>> {
  const res = await apiFetch(`reports/${encodeURIComponent(reportId)}`);
  return res.json();
}

// ── Bans ───────────────────────────────────────────────────────────────────

export type BanKind = 'clientId' | 'token';

export interface Ban {
  PK: string;
  SK: string;
  kind: BanKind;
  value: string;
  reason: string;
  createdBy: string;
  createdAt: number;
  sourceReportId?: string;
}

export async function listBans(): Promise<{ items: Ban[] }> {
  return apiJson('bans');
}

export async function createBan(input: {
  kind: BanKind;
  value: string;
  reason: string;
  sourceReportId?: string;
}): Promise<{ ban: Ban; companions: string[] }> {
  return apiJson('bans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function deleteBan(kind: BanKind, value: string): Promise<void> {
  await apiFetch(`bans/${encodeURIComponent(kind)}/${encodeURIComponent(value)}`, { method: 'DELETE' });
}

// ── Audit ──────────────────────────────────────────────────────────────────

export interface AuditEntry {
  PK: string;
  SK: string;
  actor: string;
  actorLabel?: string;
  action: string;
  target: { kind: string; value: string };
  details?: Record<string, unknown>;
  requestId: string;
  timestamp: number;
}

export async function listAudit(opts: { yyyymm?: string; cursor?: string; limit?: number } = {}): Promise<{
  items: AuditEntry[];
  yyyymm: string;
  nextCursor: string | null;
}> {
  const qs = new URLSearchParams();
  if (opts.yyyymm) qs.set('yyyymm', opts.yyyymm);
  if (opts.cursor) qs.set('cursor', opts.cursor);
  if (opts.limit) qs.set('limit', String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiJson(`audit${suffix}`);
}
